import * as React from 'react'
import { createSetPasswordActions } from '@/scopes/auth/domain/setPasswordActions'
import { createAuthApiClient } from '@/scopes/auth/infrastructure/authApiClient'
import { SetPasswordDependenciesProvider } from '@/scopes/auth/infrastructure/setPasswordDependencies'
import { createInitialSetPasswordState, createSetPasswordStore } from '@/scopes/auth/infrastructure/setPasswordStore'
import { useSetPasswordStore } from '@/scopes/auth/infrastructure/useSetPasswordStore'
import { AuthHeader } from '@/scopes/auth/pages/components/AuthHeader'
import { AuthLoading } from '@/scopes/auth/pages/components/AuthLoading'
import { SetPasswordForm } from '@/scopes/auth/pages/components/set-password/SetPasswordForm'
import { SetPasswordLinkError } from '@/scopes/auth/pages/components/set-password/SetPasswordLinkError'
import { getIsPageInitializing } from '@/scopes/auth/pages/selectors/setPasswordSelectors'
import { Footer } from '@/scopes/common/components'
import { MetaRobots } from '@/scopes/common/components/MetaRobots'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { getContextItemId } from '@/scopes/common/domain/policies/contextItemId'
import { useConfiguration, useConfigurationClient } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useLocation } from '@/scopes/common/infrastructure/dom/location'
import { createNewRelicLogger } from '@/scopes/common/infrastructure/logging/newRelicLogger'
import styles from './Auth.module.css'

function SetPasswordContent() {
  const isPageInitializing = useSetPasswordStore(getIsPageInitializing)
  const linkError = useSetPasswordStore(state => state.linkError)

  return (
    <div className={styles.layout}>
      <AuthHeader />
      <main className={styles.page}>
        {isPageInitializing
          ? <AuthLoading />
          : (
              <div className={styles.card}>
                {linkError ? <SetPasswordLinkError type={linkError} /> : <SetPasswordForm />}
              </div>
            )}
      </main>
      <Footer className={isPageInitializing ? styles.footerInitializing : undefined} />
    </div>
  )
}

export function SetPassword() {
  const location = useLocation()
  const [store] = React.useState(() => createSetPasswordStore(createInitialSetPasswordState(location.search())))
  const configurationClient = useConfigurationClient()
  const siteName = useConfiguration('siteName')
  const actions = React.useMemo(() => createSetPasswordActions({
    apiClient: createAuthApiClient({ contextItemId: getContextItemId(siteName) }),
    store,
    configurationClient,
    location,
    logger: createNewRelicLogger({ scope: 'auth' }),
  }), [store, configurationClient, location, siteName])

  React.useEffect(() => {
    void actions.pageLoaded()
  }, [actions])

  return (
    <>
      <MetaTitle titleTranslationTag="auth_meta_title" />
      <MetaRobots index={false} follow={false} />
      <SetPasswordDependenciesProvider dependencies={{ actions, store }}>
        <SetPasswordContent />
      </SetPasswordDependenciesProvider>
    </>
  )
}
